import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useGetCounts as useGetExternalCounts, useGetOperatorCounts } from "@synthesis/shared-services/external/application/hooks";
import { useGetCounts as useGetInternalCounts } from "@synthesis/shared-services/internal/application/hooks";
import type { TileCount as ExternalTileCount } from "@synthesis/shared-services/external/application/types";
import type { TileCountKeyEnumKey as InternalTileCountKey } from "@synthesis/shared-services/internal/application/types";

type ExternalTileCountKey = NonNullable<ExternalTileCount["key"]>;

import { toNumber, usePageStore, type StatCardTableFilter } from "../applications-page.lib";
import { INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL } from "../applications-page.status";
import { useClientType } from "../../../shared/hooks/use-client-type";
import { StatCard, type StatCardInfo } from "./stat-card";
import { useUserRoles } from "../../../shared/hooks/use-user-roles";

type TilePresentation = {
  label: string;
  selectedBorderClass: string;
  iconClass: string;
  icon: StatCardInfo["icon"];
  tableFilter: StatCardTableFilter;
};

const TOTAL_TILE_PRESENTATION: TilePresentation = {
  label: "Всего заявок",
  selectedBorderClass: "border-additional-turquoise-50",
  iconClass: "text-additional-turquoise-50",
  icon: "file",
  tableFilter: { type: "none" },
};

const INTERNAL_TILE_PRESENTATION: Partial<Record<InternalTileCountKey, TilePresentation>> = {
  TOTAL: TOTAL_TILE_PRESENTATION,
  NEW: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.INTAKE_CHECK,
    selectedBorderClass: "border-additional-blue-50",
    iconClass: "text-additional-blue-50",
    icon: "intakeCheck",
    tableFilter: { type: "statuses", statuses: ["INTAKE_CHECK"] },
  },
  NEEDS_ATTENTION: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.REVISION_REQUIRED,
    selectedBorderClass: "border-system-attention-50",
    iconClass: "text-system-attention-50",
    icon: "revision",
    tableFilter: { type: "statuses", statuses: ["REVISION_REQUIRED"] },
  },
  AT_NG: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.IN_NG,
    selectedBorderClass: "border-additional-yellow-50",
    iconClass: "text-additional-yellow-50",
    icon: "ngOffice",
    tableFilter: { type: "statuses", statuses: ["IN_NG"] },
  },
  AT_RAILWAY: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.IN_NTP,
    selectedBorderClass: "border-additional-orange-50",
    iconClass: "text-additional-orange-50",
    icon: "ntpLocation",
    tableFilter: { type: "statuses", statuses: ["IN_NTP"] },
  },
  SENT_TO_NG: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.SENT_TO_NG,
    selectedBorderClass: "border-additional-blue-50",
    iconClass: "text-additional-blue-50",
    icon: "ngOffice",
    tableFilter: { type: "statuses", statuses: ["SENT_TO_NG"] },
  },
  IN_NG: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.IN_NG,
    selectedBorderClass: "border-additional-violet-50",
    iconClass: "text-additional-violet-50",
    icon: "ngOffice",
    tableFilter: { type: "statuses", statuses: ["IN_NG"] },
  },
  IN_NTP: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.IN_NTP,
    selectedBorderClass: "border-additional-orange-50",
    iconClass: "text-additional-orange-50",
    icon: "ntpLocation",
    tableFilter: { type: "statuses", statuses: ["IN_NTP"] },
  },
  NG_APPROVAL: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.NG_APPROVAL,
    selectedBorderClass: "border-additional-blue-50",
    iconClass: "text-additional-blue-50",
    icon: "approvalDocument",
    tableFilter: { type: "statuses", statuses: ["NG_APPROVAL"] },
  },
  DECISION_REWORK: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.DECISION_REWORK,
    selectedBorderClass: "border-system-attention-50",
    iconClass: "text-system-attention-50",
    icon: "revision",
    tableFilter: { type: "statuses", statuses: ["DECISION_REWORK"] },
  },
};

const INTERNAL_NTP_CHIEF_TILE_PRESENTATION: Partial<Record<InternalTileCountKey, TilePresentation>> = {
  TOTAL: TOTAL_TILE_PRESENTATION,
  SENT_TO_NTP: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.SENT_TO_NTP,
    selectedBorderClass: "border-additional-violet-50",
    iconClass: "text-additional-violet-50",
    icon: "ntpLocation",
    tableFilter: { type: "statuses", statuses: ["SENT_TO_NTP"] },
  },
  IN_NTP: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.IN_NTP,
    selectedBorderClass: "border-additional-orange-50",
    iconClass: "text-additional-orange-50",
    icon: "ntpLocation",
    tableFilter: { type: "statuses", statuses: ["IN_NTP"] },
  },
  NTP_APPROVAL: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.NTP_APPROVAL,
    selectedBorderClass: "border-additional-blue-50",
    iconClass: "text-additional-blue-50",
    icon: "approvalDocument",
    tableFilter: { type: "statuses", statuses: ["NTP_APPROVAL"] },
  },
  DECISION_REWORK: INTERNAL_TILE_PRESENTATION.DECISION_REWORK!,
};

const INTERNAL_NTP_RESPONSIBLE_TILE_PRESENTATION: Partial<Record<InternalTileCountKey, TilePresentation>> = {
  TOTAL: TOTAL_TILE_PRESENTATION,
  NTP_RESP_ASSIGNED: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.NTP_RESP_ASSIGNED,
    selectedBorderClass: "border-additional-olive-50",
    iconClass: "text-additional-olive-50",
    icon: "userAssigned",
    tableFilter: { type: "statuses", statuses: ["NTP_RESP_ASSIGNED"] },
  },
  COMMISSION_PREP: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.COMMISSION_PREP,
    selectedBorderClass: "border-additional-turquoise-50",
    iconClass: "text-additional-turquoise-50",
    icon: "commission",
    tableFilter: { type: "statuses", statuses: ["COMMISSION_PREP"] },
  },
  DECISION_FORMING: {
    label: INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL.DECISION_FORMING,
    selectedBorderClass: "border-additional-turquoise-50",
    iconClass: "text-additional-turquoise-50",
    icon: "decisionForming",
    tableFilter: { type: "statuses", statuses: ["DECISION_FORMING"] },
  },
  DECISION_REWORK: INTERNAL_TILE_PRESENTATION.DECISION_REWORK!,
};

const OPERATOR_PIPELINE_TILE_PRESENTATION = {
  NEEDS_EXPORT: {
    label: "Требуется выгрузка",
    selectedBorderClass: "border-system-attention-50",
    iconClass: "text-system-attention-50",
    icon: "warningTriangle",
    tableFilter: { type: "needsExport" },
  },
  EXPORTED: {
    label: "Выгружено",
    selectedBorderClass: "border-additional-blue-50",
    iconClass: "text-additional-blue-50",
    icon: "export",
    tableFilter: { type: "operatorExported" },
  },
  LOADED: {
    label: "Загружено",
    selectedBorderClass: "border-system-success-50",
    iconClass: "text-system-success-50",
    icon: "import",
    tableFilter: { type: "operatorLoaded" },
  },
} as const satisfies Record<"NEEDS_EXPORT" | "EXPORTED" | "LOADED", TilePresentation>;

const INTERNAL_OPERATOR_TILE_PRESENTATION: Partial<Record<InternalTileCountKey, TilePresentation>> = {
  TOTAL: TOTAL_TILE_PRESENTATION,
  ...OPERATOR_PIPELINE_TILE_PRESENTATION,
};

const EXTERNAL_TILE_PRESENTATION: Partial<Record<ExternalTileCountKey, TilePresentation>> = {
  TOTAL: TOTAL_TILE_PRESENTATION,
  DRAFT: {
    label: "Черновик",
    selectedBorderClass: "border-neutrals-50",
    iconClass: "text-neutrals-50",
    icon: "draft",
    tableFilter: { type: "statuses", statuses: ["DRAFT"] },
  },
  IN_REVIEW: {
    label: "Направлено в РЖД",
    selectedBorderClass: "border-additional-blue-50",
    iconClass: "text-additional-blue-50",
    icon: "sentToRzd",
    tableFilter: { type: "statuses", statuses: ["IN_REVIEW"] },
  },
  REVISION_REQUIRED: {
    label: "Требует доработки",
    selectedBorderClass: "border-system-attention-50",
    iconClass: "text-system-attention-50",
    icon: "warningTriangle",
    tableFilter: { type: "statuses", statuses: ["REVISION_REQUIRED"] },
  },
  WAITING_PAYMENT: {
    label: "Ожидает оплаты",
    selectedBorderClass: "border-additional-olive-50",
    iconClass: "text-additional-olive-50",
    icon: "wallet",
    tableFilter: { type: "statuses", statuses: ["WAITING_PAYMENT"] },
  },
  RESOLVED_APPROVED: {
    label: "ТУ получено",
    selectedBorderClass: "border-system-success-50",
    iconClass: "text-system-success-50",
    icon: "successCircle",
    tableFilter: { type: "statuses", statuses: ["RESOLVED_APPROVED"] },
  },
  RESOLVED_REJECTED: {
    label: "Мотивированный отказ",
    selectedBorderClass: "border-system-error-50",
    iconClass: "text-system-error-50",
    icon: "rejectedCircle",
    tableFilter: { type: "statuses", statuses: ["RESOLVED_REJECTED"] },
  },
  ...OPERATOR_PIPELINE_TILE_PRESENTATION,
};

const getStatCardValue = (
  tileCount: number | bigint | null | undefined,
  tableFilter: StatCardTableFilter,
  byStatus?: { [key: string]: number } | null,
): number => {
  if (tableFilter.type === "statuses" && byStatus) {
    const status = tableFilter.statuses[0];

    if (status !== undefined && tableFilter.statuses.length === 1) {
      return toNumber(byStatus[status] ?? 0);
    }
  }

  return toNumber(tileCount);
};

const buildStatCardsFromTiles = <TKey extends string>(
  tiles: Array<{ key?: TKey | null; count?: number | bigint | null }> | null | undefined,
  presentationByKey: Partial<Record<TKey, TilePresentation>>,
  byStatus?: { [key: string]: number } | null,
): StatCardInfo[] => {
  if (!tiles?.length) {
    return [];
  }

  return tiles.flatMap((tile) => {
    if (!tile.key) {
      return [];
    }

    const presentation = presentationByKey[tile.key];

    if (!presentation) {
      return [];
    }

    return [
      {
        filterKey: tile.key,
        value: getStatCardValue(tile.count, presentation.tableFilter, byStatus),
        ...presentation,
      },
    ];
  });
};

const isCardSelected = (card: StatCardInfo, statCardFilterKey: string) => card.filterKey === statCardFilterKey;

type StatsSectionRole =
  | "external-applicant"
  | "external-operator"
  | "internal-ng-secretary"
  | "internal-apparatus-ng"
  | "internal-ntp-chief"
  | "internal-ntp-responsible"
  | "internal-operator";

const INTERNAL_STATS_SECTION_ROLES = new Set<StatsSectionRole>([
  "internal-ng-secretary",
  "internal-apparatus-ng",
  "internal-ntp-chief",
  "internal-ntp-responsible",
  "internal-operator",
]);

const isInternalStatsSectionRole = (role: StatsSectionRole | null): role is Exclude<StatsSectionRole, "external-applicant" | "external-operator"> =>
  role !== null && INTERNAL_STATS_SECTION_ROLES.has(role);

const getInternalTilePresentation = (role: StatsSectionRole): Partial<Record<InternalTileCountKey, TilePresentation>> => {
  switch (role) {
    case "internal-ntp-chief":
      return INTERNAL_NTP_CHIEF_TILE_PRESENTATION;
    case "internal-ntp-responsible":
      return INTERNAL_NTP_RESPONSIBLE_TILE_PRESENTATION;
    case "internal-operator":
      return INTERNAL_OPERATOR_TILE_PRESENTATION;
    default:
      return INTERNAL_TILE_PRESENTATION;
  }
};

const resolveStatsSectionRole = (clientType: ReturnType<typeof useClientType>, roles: ReturnType<typeof useUserRoles>): StatsSectionRole | null => {
  if (clientType === "external") {
    if (roles.isExternalOperator) {
      return "external-operator";
    }

    return "external-applicant";
  }

  if (roles.isInternalNgSecretary) {
    return "internal-ng-secretary";
  }

  if (roles.isInternalApparatusNg) {
    return "internal-apparatus-ng";
  }

  if (roles.isInternalNtpChief) {
    return "internal-ntp-chief";
  }

  if (roles.isInternalNtpResponsible) {
    return "internal-ntp-responsible";
  }

  if (roles.isInternalOperator) {
    return "internal-operator";
  }

  return null;
};

export const ApplicationsStatsSection = observer(() => {
  const clientType = useClientType();
  const pageStore = usePageStore();
  const roles = useUserRoles();
  const statsSectionRole = resolveStatsSectionRole(clientType, roles);
  const externalApplicantCountsQuery = useGetExternalCounts({
    query: {
      enabled: statsSectionRole === "external-applicant",
    },
  });
  const externalOperatorCountsQuery = useGetOperatorCounts({
    query: {
      enabled: statsSectionRole === "external-operator",
    },
  });
  const internalCountsQuery = useGetInternalCounts({
    query: {
      enabled: isInternalStatsSectionRole(statsSectionRole),
    },
  });
  const statCards = useMemo(() => {
    switch (statsSectionRole) {
      case "internal-ng-secretary":
      case "internal-apparatus-ng":
      case "internal-ntp-chief":
      case "internal-ntp-responsible":
      case "internal-operator":
        return buildStatCardsFromTiles(
          internalCountsQuery.data?.tiles,
          getInternalTilePresentation(statsSectionRole),
          internalCountsQuery.data?.byStatus,
        );
      case "external-operator":
        return buildStatCardsFromTiles(externalOperatorCountsQuery.data?.tiles, EXTERNAL_TILE_PRESENTATION);
      case "external-applicant":
        return buildStatCardsFromTiles(externalApplicantCountsQuery.data?.tiles, EXTERNAL_TILE_PRESENTATION);
      default:
        return [];
    }
  }, [externalApplicantCountsQuery.data, externalOperatorCountsQuery.data, internalCountsQuery.data, statsSectionRole]);

  const isCountsLoading = useMemo(() => {
    switch (statsSectionRole) {
      case "external-applicant":
        return externalApplicantCountsQuery.isPending;
      case "external-operator":
        return externalOperatorCountsQuery.isPending;
      case "internal-ng-secretary":
      case "internal-apparatus-ng":
      case "internal-ntp-chief":
      case "internal-ntp-responsible":
      case "internal-operator":
        return internalCountsQuery.isPending;
      default:
        return false;
    }
  }, [externalApplicantCountsQuery.isPending, externalOperatorCountsQuery.isPending, internalCountsQuery.isPending, statsSectionRole]);

  useEffect(() => {
    const isCurrentFilterValid = statCards.some((card) => card.filterKey === pageStore.statCardFilterKey);

    if (!isCurrentFilterValid) {
      pageStore.setStatCardFilter("TOTAL", { type: "none" });
    }
  }, [pageStore, statCards]);

  const handleCardClick = (card: StatCardInfo) => {
    if (isCardSelected(card, pageStore.statCardFilterKey)) {
      pageStore.setStatCardFilter("TOTAL", { type: "none" });
      return;
    }

    pageStore.setStatCardFilter(card.filterKey, card.tableFilter);
  };

  if (!statsSectionRole) {
    return null;
  }

  if (isCountsLoading) {
    return (
      <div className="pb-1">
        <div aria-busy="true" aria-label="Загрузка счётчиков" className="skeleton h-[2.625rem] w-full" />
      </div>
    );
  }

  if (statCards.length === 0) {
    return null;
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-1">
      {statCards.map((card) => (
        <StatCard
          key={card.filterKey}
          card={card}
          isSelected={isCardSelected(card, pageStore.statCardFilterKey)}
          onClick={() => handleCardClick(card)}
        />
      ))}
    </div>
  );
});
